import React, { useEffect, useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { LineChart as LineIcon, TrendingUp, Activity } from "lucide-react";

const RANGES = [["30d", "30 Days"], ["90d", "90 Days"], ["1y", "1 Year"], ["all", "All Time"]];

/* ---------------- Development Summary Card (prominent, near top) ---------------- */
export function DevelopmentSummary({ playerId }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get(`/players/${playerId}/development-summary`).then(({ data }) => setS(data)).catch(() => setS({ status: "gathering" })); }, [playerId]);
  if (!s) return null;

  const gathering = s.status === "gathering";
  const rows = [];
  if (!gathering) {
    (s.metrics || []).forEach((m) => rows.push({ label: m.label, text: `${m.delta > 0 ? "+" : ""}${m.delta} ${m.units || ""}`.trim(), improved: m.improved }));
    (s.rankings || []).forEach((r) => rows.push({ label: r.label, text: `${r.improved ? "↑" : "↓"} ${Math.abs(r.places)} places`, improved: r.improved }));
    if (s.rating) rows.push({ label: "CBR Rating", text: `${s.rating.delta > 0 ? "+" : ""}${s.rating.delta}`, improved: s.rating.improved });
  }

  return (
    <div data-testid="development-summary" className="bg-gradient-to-br from-[#0A192F] to-[#12233f] text-white rounded-sm p-6 border border-white/10">
      <div className="flex items-center gap-2 label-overline text-white/60"><TrendingUp className="w-4 h-4 text-[#E63946]" /> Development Since First CBR Event</div>
      {gathering ? (
        <div className="flex items-center gap-2 py-6 text-white/50 text-sm"><Activity className="w-4 h-4 animate-pulse" /> Collecting historical data…</div>
      ) : rows.length === 0 ? (
        <p className="py-4 text-white/50 text-sm">No measurable change recorded yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3 mt-4">
          {rows.map((r, i) => (
            <div key={i} data-testid={`dev-summary-row-${i}`}>
              <div className="text-[11px] text-white/50 uppercase tracking-wider">{r.label}</div>
              <div className={`font-data text-xl mt-0.5 ${r.improved ? "text-emerald-400" : "text-amber-400"}`}>{r.text}</div>
            </div>
          ))}
        </div>
      )}
      {!gathering && s.since_date && <p className="text-[11px] text-white/30 mt-4">Baseline: {new Date(s.since_date).toLocaleDateString("en-CA", { dateStyle: "medium" })}</p>}
    </div>
  );
}

/* ---------------- Development Charts ---------------- */
const RANK_KEYS = new Set(["national", "provincial", "position"]);

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[#0A192F] text-white text-xs rounded-sm px-3 py-2 shadow-lg">
      <div className="font-data text-base">{p.value}</div>
      {p.event_name && <div className="text-white/70">{p.event_name}</div>}
      <div className="text-white/40">{new Date(label).toLocaleDateString("en-CA", { dateStyle: "medium" })}</div>
    </div>
  );
}

export function DevelopmentCharts({ playerId }) {
  const [range, setRange] = useState("all");
  const [d, setD] = useState(null);
  const [sel, setSel] = useState("rating");

  useEffect(() => { api.get(`/players/${playerId}/development?range=${range}`).then(({ data }) => setD(data)).catch(() => setD(null)); }, [playerId, range]);

  const options = useMemo(() => {
    if (!d) return [];
    const base = [["rating", "CBR Rating"], ["national", "National Ranking"], ["provincial", "Provincial Ranking"], ["position", "Position Ranking"]];
    const metrics = (d.metric_keys || []).map((k) => [`m:${k}`, k.replace(/_/g, " ")]);
    return [...base, ...metrics];
  }, [d]);

  const series = useMemo(() => {
    if (!d) return [];
    if (sel === "rating") return d.rating || [];
    if (RANK_KEYS.has(sel)) return d.rankings?.[sel] || [];
    if (sel.startsWith("m:")) return d.metrics?.[sel.slice(2)] || [];
    return [];
  }, [d, sel]);

  const isRank = RANK_KEYS.has(sel);
  const enough = series.length >= 2;

  return (
    <div data-testid="development-charts">
      <div className="label-overline text-[#E63946]">Player Development</div>
      <h3 className="font-display text-2xl uppercase tracking-tight mt-1 mb-3">Progression Over Time</h3>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select data-testid="dev-metric-select" value={sel} onChange={(e) => setSel(e.target.value)} className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm bg-white">
          {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="flex gap-1 ml-auto">
          {RANGES.map(([v, l]) => (
            <button key={v} data-testid={`dev-range-${v}`} onClick={() => setRange(v)} className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider rounded-sm ${range === v ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4">
        {!enough ? (
          <div className="h-64 flex flex-col items-center justify-center text-slate-400 gap-2" data-testid="dev-gathering">
            <Activity className="w-6 h-6 animate-pulse" /> <span className="text-sm">Collecting historical data…</span>
            <span className="text-xs text-slate-300">Charts populate automatically as CBR records more data.</span>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={series} margin={{ top: 8, right: 12, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => new Date(v).toLocaleDateString("en-CA", { month: "short", day: "numeric" })} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} reversed={isRank} domain={isRank ? [1, "dataMax"] : ["auto", "auto"]} allowDecimals={!isRank} width={40} />
              <Tooltip content={<ChartTooltip />} />
              <Line type="monotone" dataKey="value" stroke="#E63946" strokeWidth={2.5} dot={{ r: 3, fill: "#E63946" }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        )}
        {isRank && enough && <p className="text-[11px] text-slate-400 mt-2">Lower is better — a line trending upward on the chart means the player is climbing the rankings.</p>}
      </div>
    </div>
  );
}
